import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import walletService from '../../services/walletService';
import diamantService from '../../services/diamantService';

import './WalletPage.css';

const WalletPage = () => {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [usages, setUsages] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWalletData();
  }, []);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const [walletRes, transactionsRes, usagesRes, earningsRes] = await Promise.all([
        walletService.getWallet(),
        walletService.getTransactions(20),
        walletService.getUsages(20),
        diamantService.getEarningHistory(20)

      ]);

      if (walletRes.data?.success) setWallet(walletRes.data.data);
      if (transactionsRes.data?.success) setTransactions(transactionsRes.data.data || []);
      if (usagesRes.data?.success) setUsages(usagesRes.data.data || []);
      if (earningsRes.data?.success) setEarnings(earningsRes.data.data || []);
    } catch (error) {
      console.error('Error fetching wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'purchase': return '💰';
      case 'usage': return '💸';
      case 'earning': return '🎁';
      case 'gift_sent': return '🎀';
      case 'gift_received': return '🎊';
      case 'refund': return '↩️';
      default: return '💎';


    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'purchase':
      case 'earning':
      case 'welcome_bonus':
      case 'daily_login':
      case 'referral':
      case 'loveshot_reward':
      case 'gift_received':
      case 'refund':
        return 'positive';
      case 'usage':
      case 'gift_sent':
      case 'certification':
      case 'message':
      case 'profile_boost':
      case 'loveshot_boost':
        return 'negative';
      default:
        return 'neutral';
    }
  };


  // Safe number formatter
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="wallet-page loading">
        <div className="spinner"></div>
        <p>Chargement de votre portefeuille...</p>
      </div>
    );
  }

  // Default wallet values
  const walletData = wallet || {};
  const balance = walletData.balance || 0;
  const totalEarned = walletData.total_earned || 0;
  const totalSpent = walletData.total_spent || 0;
  const totalPurchased = walletData.total_purchased || 0;
  const isPremium = walletData.is_premium || false;

  return (
    <div className="wallet-page">
      <div className="wallet-header">
        <h1>💎 Mon Portefeuille</h1>


        <Link to="/wallet/store" className="btn-buy">
          + Acheter des Diamants
        </Link>

      </div>

      <div className="wallet-balance-card">
        <div className="balance-main">
          <span className="balance-icon-large">💎</span>


          <div className="balance-details">
            <span className="balance-total">{formatNumber(balance)}</span>
            <span className="balance-text">Diamants disponibles</span>

          </div>
        </div>
        
        <div className="balance-stats">
          <div className="stat-item">
            <span className="stat-value">{formatNumber(totalEarned)}</span>
            <span className="stat-label">Total gagné</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{formatNumber(totalSpent)}</span>
            <span className="stat-label">Total dépensé</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{formatNumber(totalPurchased)}</span>
            <span className="stat-label">Total acheté</span>
          </div>
        </div>

        {isPremium && (
          <div className="premium-banner">
            <span className="premium-icon">⭐</span>
            <span>Statut Premium actif</span>
          </div>
        )}
      </div>

      <div className="wallet-tabs">
        <button 
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Vue d'ensemble
        </button>
        <button 
          className={activeTab === 'transactions' ? 'active' : ''}
          onClick={() => setActiveTab('transactions')}
        >
          Transactions
        </button>
        <button 
          className={activeTab === 'usages' ? 'active' : ''}
          onClick={() => setActiveTab('usages')}
        >
          Dépenses
        </button>
        <button 
          className={activeTab === 'earnings' ? 'active' : ''}
          onClick={() => setActiveTab('earnings')}
        >
          Gains
        </button>
      </div>

      <div className="wallet-content">
        {activeTab === 'overview' && (
          <div className="overview-section">
            <div className="quick-actions">
              <Link to="/wallet/store" className="action-card">
                <span className="action-icon">🛒</span>
                <span className="action-text">Acheter des Diamants</span>

              </Link>
              <Link to="/gifts" className="action-card">
                <span className="action-icon">🎁</span>
                <span className="action-text">Envoyer un cadeau</span>
              </Link>
              <div className="action-card" onClick={() => setActiveTab('earnings')}>
                <span className="action-icon">🎯</span>
                <span className="action-text">Gagner des Diamants</span>

              </div>
            </div>

            <div className="recent-activity">
              <h3>Activité récente</h3>
              {transactions.length === 0 ? (
                <p className="no-data">Aucune activité récente</p>
              ) : (
                transactions.slice(0, 5).map((tx) => (
                  <div key={tx.id} className="activity-item">
                    <span className="activity-icon">{getTransactionIcon(tx.type)}</span>
                    <div className="activity-details">
                      <span className="activity-title">{tx.description || 'Transaction'}</span>
                      <span className="activity-date">
                        {tx.created_at ? new Date(tx.created_at).toLocaleDateString('fr-FR') : '-'}
                      </span>
                    </div>
                    <span className={`activity-amount ${getTransactionColor(tx.type)}`}>
                      {tx.type === 'purchase' || tx.type === 'earning' || tx.type === 'gift_received' ? '+' : '-'}
                      {Math.abs(tx.amount) || 0} Diamants

                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className="transactions-list">
            <h3>Historique des transactions</h3>
            {transactions.length === 0 ? (
              <p className="no-data">Aucune transaction</p>
            ) : (
              transactions.map((tx) => (
                <div key={tx.id} className="transaction-item">
                  <div className="transaction-icon">{getTransactionIcon(tx.type)}</div>
                  <div className="transaction-info">
                    <span className="transaction-type">{tx.description || 'Transaction'}</span>
                    <span className="transaction-meta">
                      {tx.created_at ? new Date(tx.created_at).toLocaleString('fr-FR') : '-'} • {tx.status || 'completed'}
                    </span>
                  </div>
                  <div className={`transaction-amount ${getTransactionColor(tx.type)}`}>
                    {tx.type === 'purchase' || tx.type === 'earning' || tx.type === 'welcome_bonus' || tx.type === 'daily_login' || tx.type === 'referral' || tx.type === 'loveshot_reward' || tx.type === 'gift_received' || tx.type === 'refund' ? '+' : '-'}
                    {Math.abs(tx.amount) || 0}
                  </div>


                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'usages' && (
          <div className="usages-list">
            <h3>Historique des dépenses</h3>
            {usages.length === 0 ? (
              <p className="no-data">Aucune dépense</p>
            ) : (
              usages.map((usage) => (
                <div key={usage.id} className="usage-item">
                  <div className="usage-icon">💸</div>
                  <div className="usage-info">
                    <span className="usage-feature">{usage.feature_type || 'Utilisation'}</span>
                    <span className="usage-description">{usage.description || '-'}</span>
                    <span className="usage-date">
                      {usage.created_at ? new Date(usage.created_at).toLocaleString('fr-FR') : '-'}
                    </span>
                  </div>
                  <div className="usage-amount negative">-{Math.abs(usage.amount) || 0}</div>

                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'earnings' && (
          <div className="earnings-list">
            <h3>Historique des gains</h3>
            {earnings.length === 0 ? (
              <p className="no-data">Aucun gain</p>
            ) : (
              earnings.map((earning) => (
                <div key={earning.id} className="earning-item">
                  <div className="earning-icon">🎁</div>
                  <div className="earning-info">
                    <span className="earning-source">{earning.source || 'Gain'}</span>
                    <span className="earning-description">{earning.description || '-'}</span>
                    <span className="earning-date">
                      {earning.created_at ? new Date(earning.created_at).toLocaleString('fr-FR') : '-'}
                    </span>
                  </div>
                  <div className="earning-amount positive">+{earning.amount || 0}</div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletPage;
